import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { OrgContext } from "../../../context/OrgContext";
import moment from "moment";
import LpField from "../../../components/common/linchpin/LpField";
import LpInput from "../../../components/common/linchpin/LpInput";
import LpSelect from "../../../components/common/linchpin/LpSelect";
import LpButton from "../../../components/common/linchpin/LpButton";
import MaterialTimePicker from "../../../components/orders/MaterialTimePicker";
import {
  detectCountryFromPhone,
  dialCodeByCountry,
  dialDisplayByCountry,
} from "../../../components/common/ReusableMobileInput";
import { createPredefinedStop } from "../../../api/LocationsApi";
import { alertMessage } from "../../../common/Common";
import PredefinedLocationIcon from "../../../../assets/images/PredefinedLocation.svg";
import CancelIcon from "../../../../assets/images/CancelIcon.svg";
import CommercialIcon from "../../../../assets/images/Commercial.svg";
import LocIconMarker from "../../../../assets/images/LocIconMarker.svg";
import UserIcon from "../../../../assets/images/UserIcon.svg";
import EmailIcon from "../../../../assets/images/EmailIcon.svg";
import PhoneIcon from "../../../../assets/images/PhoneIcon.svg";
import ArrowDownIcon from "../../../../assets/images/ArrowDownIcon.svg";
import "./SavePredefinedModal.css";

// Operating hours are stored as a plain "HH:mm" time-of-day string — that's what
// the BE consumers expect (e.g. customer_orders.rb does `start_time.split(':')`,
// bulk_appointment_schedules defaults to '08:00'/'20:00'). Storing a full ISO
// datetime broke those (split on the ISO string) and shifted the time to UTC.
const parseOperationalTime = (timeValue) => {
  if (!timeValue) return null;
  if (moment.isMoment(timeValue)) return timeValue;
  // Accept "HH:mm" (current shape) and, for legacy records, an ISO datetime.
  const hm = moment(timeValue, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  if (hm.isValid()) return hm;
  const iso = moment(timeValue, moment.ISO_8601, true);
  return iso.isValid() ? iso : null;
};
const normalizeOperationalTime = (timeValue) => {
  if (!timeValue) return "";
  const m = moment.isMoment(timeValue) ? timeValue : moment(timeValue);
  return m.isValid() ? m.format("HH:mm") : "";
};

// Mirrors AddressForm_v2.jsx:71-75 (single source of truth for the org-wide
// list — keep in sync if more options are added there).
const OPERATIONAL_USE_OPTIONS = [
  { value: "FINAL_MILE", label: "Cartage/White Glove" },
  { value: "DRAYAGE", label: "Drayage" },
  { value: "BOTH", label: "Both" },
];

// Module-level constants so JSX literals don't allocate new objects/functions
// on every render — keeps MaterialTimePicker's prop identity stable.
const TP_INPUT_STYLE = { padding: "4px 12px", fontSize: "14px", height: "22px" };
const TP_STYLE = { width: "100%" };
const NO_DISABLED = () => [];
// Ant Design modal z-index is ~1000; keep the MUI picker above it.
const MODAL_POPPER_PROPS = { style: { zIndex: 10050 } };

const formatNationalNumber = (digits, countryCode) => {
  const dial = dialCodeByCountry(countryCode);
  const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
  if (!national) return digits;

  if (countryCode === "in" && national.length >= 10) {
    return `${national.slice(0, 5)}-${national.slice(5, 10)}`;
  }
  if (countryCode === "mx" && national.length >= 10) {
    return `${national.slice(0, 2)} ${national.slice(2, 6)}-${national.slice(6, 10)}`;
  }
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return national;
};

const formatPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  const country = detectCountryFromPhone(digits);
  const dial = dialDisplayByCountry(country);
  const national = formatNationalNumber(digits, country);
  return `${dial} ${national}`;
};

const trimmed = (v) => String(v == null ? "" : v).trim();
const hasText = (v) => !!trimmed(v);
const hasPhone = (v) => !!formatPhone(v);

const formattedAddress = (loc) =>
  [
    loc.customer_address_line_1,
    loc.customer_address_line_2,
    loc.customer_city,
    [loc.customer_state, loc.customer_zipcode].filter(Boolean).join(" "),
    loc.customer_country,
  ]
    .filter((s) => s && String(s).trim())
    .join(", ");

const buildPayload = (loc, form, orgId) => {
  const name =
    loc.company_name ||
    [loc.customer_first_name, loc.customer_last_name].filter(Boolean).join(" ") ||
    form.location_code;
  return {
    organization_id: orgId,
    name,
    l_type: "PS",
    company_name: loc.company_name || "",
    first_name: loc.customer_first_name || "",
    last_name: loc.customer_last_name || "",
    email: loc.customer_email || "",
    phone: loc.customer_phone_one || "",
    phone_2: loc.customer_phone_two || "",
    notes: loc.notes || "",
    address_type: loc.address_type || "COMMERCIAL",
    operational_use: form.operational_use,
    location_code: form.location_code,
    start_time: form.start_time || "",
    end_time: form.end_time || "",
    l_address: {
      address_line1: loc.customer_address_line_1 || "",
      address_line2: loc.customer_address_line_2 || "",
      zipcode: loc.customer_zipcode || "",
      city: loc.customer_city || "",
      state: loc.customer_state || "",
      country: loc.customer_country || "",
    },
  };
};

function SavePredefinedModal({ visible, onCancel, onSaved, location = {}, orgId, isMilitaryTime = false }) {
  // Operational Use only applies to drayage-enabled orgs (same gate the
  // Predefined Stops screen uses — AddressForm_v2.jsx). Everyone else saves
  // the backend default FINAL_MILE without ever seeing the field.
  const { orgSettings = {} } = useContext(OrgContext);
  const drayageEnabled = orgSettings?.drayage_enabled === "true";

  const [form, setForm] = useState({
    location_code: "",
    operational_use: "FINAL_MILE",
    start_time: "",
    end_time: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setForm({
        location_code: location.location_code || "",
        operational_use: drayageEnabled ? location.operational_use || "FINAL_MILE" : "FINAL_MILE",
        start_time: location.start_time || "",
        end_time: location.end_time || "",
      });
    }
  }, [visible, location.location_code, location.operational_use, location.start_time, location.end_time, drayageEnabled]);

  // Build one stable handler per field so the LpInput/LpSelect/MaterialTimePicker
  // children keep the same onChange identity across renders — without this the
  // modal re-renders every input on every keystroke. The handler reads
  // e.target.value synchronously (React 16 SyntheticEvent pooling) and writes
  // through a functional setForm so the latest prev state always wins.
  const fieldHandlers = useMemo(() => {
    const make = (key) => (e) => {
      const v = e && e.target ? e.target.value : e;
      setForm((prev) => ({ ...prev, [key]: v }));
    };
    return {
      location_code: make("location_code"),
      operational_use: make("operational_use"),
      start_time: (v) =>
        setForm((prev) => ({ ...prev, start_time: normalizeOperationalTime(v) })),
      end_time: (v) =>
        setForm((prev) => ({ ...prev, end_time: normalizeOperationalTime(v) })),
    };
  }, []);

  // Memoize parsed moment values so MaterialTimePicker's `value` prop only
  // changes when the underlying ISO string changes — avoids its internal
  // useEffect resetting picker state on every keystroke.
  const startTimeMoment = useMemo(
    () => parseOperationalTime(form.start_time),
    [form.start_time]
  );
  const endTimeMoment = useMemo(
    () => parseOperationalTime(form.end_time),
    [form.end_time]
  );

  const codeMissing = !String(form.location_code || "").trim();
  const opUseMissing = !form.operational_use;
  const canSave = !codeMissing && !opUseMissing && !saving;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = buildPayload(location, form, orgId);
      const res = await createPredefinedStop(payload);
      if (res?.success) {
        alertMessage("Saved as predefined location", "success", 5);
        if (onSaved) onSaved(res.location || res);
        onCancel();
      } else {
        const msg = Array.isArray(res?.errors) ? res.errors.join(", ") : (res?.errors || "Failed to save predefined location");
        alertMessage(msg, "error", 10);
      }
    } catch (err) {
      alertMessage(err?.message || "Failed to save predefined location", "error", 10);
    } finally {
      setSaving(false);
    }
  }, [canSave, location, form, orgId, onSaved, onCancel]);

  const isResidential =
    typeof location.address_type === "string" &&
    ["RESIDENTIAL", "RESEDENTIAL"].includes(location.address_type.toUpperCase());
  const addrTypeLabel = isResidential ? "Residential" : "Commercial";
  const displayName =
    location.company_name ||
    [location.customer_first_name, location.customer_last_name].filter(Boolean).join(" ") ||
    "Location";

  const hasContactName =
    hasText(location.customer_first_name) || hasText(location.customer_last_name);
  const hasContactEmail = hasText(location.customer_email);
  const hasContactPhoneOne = hasPhone(location.customer_phone_one);
  const hasContactPhoneTwo = hasPhone(location.customer_phone_two);
  const hasContacts =
    hasContactName || hasContactEmail || hasContactPhoneOne || hasContactPhoneTwo;

  return (
    <Modal
      className="spm-modal"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable={false}
      maskClosable={false}
      destroyOnClose
      width={536}
      centered
    >
      <div className="lp-v3 spm-root">
        <div className="spm-head">
          <div className="spm-head-left">
            <span className="spm-head-icon" aria-hidden="true">
              <img src={PredefinedLocationIcon} alt="" width="24" height="32" />
            </span>
            <h3 className="spm-title">Save as Predefined Location</h3>
          </div>
          <button type="button" className="lp-icon-btn spm-close" aria-label="Close" onClick={onCancel}>
            <img src={CancelIcon} alt="" className="spm-close-icon" aria-hidden="true" />
          </button>
        </div>

        <div className="spm-body">
          <div className="spm-summary">
            <div className="spm-summary-inner">
              <div className="spm-summary-top">
                <div className="spm-summary-name-block">
                  <div className="spm-summary-title">{displayName}</div>
                  <div className="spm-summary-meta">
                    <span className="spm-summary-meta-item">
                      <img src={CommercialIcon} alt="" className="spm-summary-icon" />
                      <span>{addrTypeLabel}</span>
                    </span>
                    {form.location_code && (
                      <span className="spm-summary-meta-item">
                        <img src={LocIconMarker} alt="" className="spm-summary-icon" />
                        <span>Location Code: {form.location_code}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="spm-summary-addr">{formattedAddress(location)}</div>
              </div>
              {hasContacts ? (
                <div className="spm-summary-contacts">
                  {hasContactName ? (
                    <span className="spm-summary-contact">
                      <img src={UserIcon} alt="" className="spm-summary-icon" />
                      <span>
                        {[location.customer_first_name, location.customer_last_name].filter(hasText).join(" ")}
                      </span>
                    </span>
                  ) : null}
                  {hasContactEmail ? (
                    <span className="spm-summary-contact">
                      <img src={EmailIcon} alt="" className="spm-summary-icon" />
                      <span className="spm-summary-link">{trimmed(location.customer_email)}</span>
                    </span>
                  ) : null}
                  {hasContactPhoneOne ? (
                    <span className="spm-summary-contact">
                      <img src={PhoneIcon} alt="" className="spm-summary-icon" />
                      <span className="spm-summary-link">{formatPhone(location.customer_phone_one)}</span>
                    </span>
                  ) : null}
                  {hasContactPhoneTwo ? (
                    <span className="spm-summary-contact">
                      <img src={PhoneIcon} alt="" className="spm-summary-icon" />
                      <span className="spm-summary-link">{formatPhone(location.customer_phone_two)}</span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="spm-row spm-row--fields">
            <div className={`${drayageEnabled ? "spm-col-2" : "spm-col-4"} spm-field`}>
              <LpField label="Location Code" required>
                <LpInput
                  value={form.location_code}
                  onChange={fieldHandlers.location_code}
                  placeholder="e.g. PCS"
                  disabled={saving}
                />
              </LpField>
            </div>
            {drayageEnabled && (
              <div className="spm-col-2 spm-field">
                <LpField label="Operational Use" required>
                  <LpSelect
                    className="spm-select"
                    options={OPERATIONAL_USE_OPTIONS}
                    value={form.operational_use}
                    onChange={fieldHandlers.operational_use}
                    disabled={saving}
                    caretIcon={<img src={ArrowDownIcon} alt="" className="spm-select-caret" />}
                  />
                </LpField>
              </div>
            )}
          </div>

          <div className="spm-section-label">Operating Hours</div>

          <div className="spm-row spm-row--times">
            <div className="spm-col-2 spm-field">
              <LpField label="Start Time">
                <MaterialTimePicker
                  className="spm-tp"
                  style={TP_STYLE}
                  inputStyle={TP_INPUT_STYLE}
                  value={startTimeMoment}
                  onChange={fieldHandlers.start_time}
                  isMilitaryTime={isMilitaryTime}
                  computeDisabledHours={NO_DISABLED}
                  computeDisabledMinutes={NO_DISABLED}
                  popperProps={MODAL_POPPER_PROPS}
                  disabled={saving}
                />
              </LpField>
            </div>
            <div className="spm-col-2 spm-field">
              <LpField label="End Time">
                <MaterialTimePicker
                  className="spm-tp"
                  style={TP_STYLE}
                  inputStyle={TP_INPUT_STYLE}
                  value={endTimeMoment}
                  onChange={fieldHandlers.end_time}
                  isMilitaryTime={isMilitaryTime}
                  computeDisabledHours={NO_DISABLED}
                  computeDisabledMinutes={NO_DISABLED}
                  popperProps={MODAL_POPPER_PROPS}
                  disabled={saving}
                />
              </LpField>
            </div>
          </div>
        </div>

        <div className="spm-foot">
          <LpButton variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </LpButton>
          <LpButton variant="primary" size="sm" onClick={handleSave} disabled={!canSave}>
            {saving ? "Saving…" : "Save"}
          </LpButton>
        </div>
      </div>
    </Modal>
  );
}

export default React.memo(SavePredefinedModal);
